'use client';
import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { InstitutionComparison } from '@/lib/types';

interface Props {
  data: InstitutionComparison[];
}

const shortName = (nome: string): string => {
  const map: Record<string, string> = {
    'Caixa Geral de Depósitos': 'CGD',
    'Millennium BCP': 'BCP',
    'Novo Banco': 'NB',
    'Santander Portugal': 'Santander',
    'Banco BPI': 'BPI',
    'Activobank': 'Activo',
    'Abanca Portugal': 'Abanca',
  };
  return map[nome] ?? nome.split(' ')[0];
};

interface TooltipPayload {
  value: number;
  payload: { nome: string; valorPago: number; status: string };
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded-lg shadow-lg px-3 py-2.5 text-sm min-w-[180px]">
      <p className="font-semibold text-foreground mb-1">{d.nome}</p>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground text-xs">Valor Pago:</span>
        <span className={`font-semibold tabular-nums text-xs ${d.valorPago >= 0 ? 'text-positive' : 'text-negative'}`}>
          {d.valorPago >= 0
            ? `€ ${d.valorPago.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`
            : `(€ ${Math.abs(d.valorPago).toLocaleString('pt-PT', { minimumFractionDigits: 2 })})`
          }
        </span>
      </div>
      {d.status === 'anomalia' && (
        <p className="text-[11px] text-warning-color mt-1 border-t border-border pt-1">⚠ Anomalia detectada</p>
      )}
    </div>
  );
}

export default function PaymentsBarChart({ data }: Props) {
  const chartData = data.map((d) => ({
    nome: d.nome,
    shortNome: shortName(d.nome),
    valorPago: d.valorPago,
    status: d.status,
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 4 }} barSize={28}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
        <XAxis
          dataKey="shortNome"
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
          width={42}
        />
        <ReferenceLine y={0} stroke="var(--border)" strokeWidth={1.5} />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
        <Bar dataKey="valorPago" radius={[4, 4, 0, 0]}>
          {chartData.map((entry, index) => (
            <Cell
              key={`bar-cell-${index}`}
              fill={
                entry.status === 'anomalia' ?'var(--warning)'
                  : entry.valorPago < 0
                  ? 'var(--negative)'
                  : 'var(--primary)'
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}